import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography,
  TextField, MenuItem, Switch, FormControlLabel, Button, IconButton, Tabs, Tab,
  InputAdornment, Chip, Divider, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  IEmailTemplate, IEmailTemplateMeta, IEmailTemplateInput,
  CommunicationTemplateType, CommunicationTemplateVisibility,
  createEmailTemplate, updateEmailTemplate, previewEmailTemplate,
  IAIGenerateResult,
} from "../../../Api/emailTemplatesApi";
import AIGenerateDialog from "./AIGenerateDialog";

const NAVY = "#0F172A";
const HAIRLINE = "rgba(10,14,26,0.06)";

interface Props {
  open: boolean;
  template: IEmailTemplate | null;
  meta: IEmailTemplateMeta | null;
  onClose: () => void;
  onSaved: (tpl: IEmailTemplate, message: string) => void;
}

/* The Quill toolbar is intentionally compact — every staff template
 * stays composed of standard rich text elements (headings, lists,
 * links, inline color); the goal is "professional letter", not
 * "newsletter graphic". */
const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 2, 3] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    [{ color: [] }],
    ["link"],
    ["clean"],
  ],
};

/** What the form holds while editing. Mirrors IEmailTemplateInput +
 *  a couple of UI-only fields. */
interface FormState {
  name: string;
  slug: string;
  subject: string;
  description: string;
  body: string;
  category: string;
  template_type: CommunicationTemplateType;
  visibility: CommunicationTemplateVisibility;
  is_active: boolean;
  is_default: boolean;
}

const blankForm = (): FormState => ({
  name: "",
  slug: "",
  subject: "",
  description: "",
  body: "",
  category: "general",
  template_type: "email",
  visibility: "customer_facing",
  is_active: true,
  is_default: false,
});

const TemplateEditor: React.FC<Props> = ({ open, template, meta, onClose, onSaved }) => {
  const isEdit = Boolean(template?.id);
  const [form, setForm] = useState<FormState>(() =>
    template
      ? {
          name: template.name,
          slug: template.slug,
          subject: template.subject,
          description: template.description ?? "",
          body: template.body,
          category: template.category ?? "general",
          template_type: template.template_type ?? "email",
          visibility: template.visibility ?? "customer_facing",
          is_active: template.is_active,
          is_default: template.is_default,
        }
      : blankForm()
  );

  // Only the email type carries a customer subject line + uses Quill.
  // Activity types (call_log/internal_note/…) are plain text and the
  // subject field is hidden. Computed here so the JSX stays readable.
  const isEmailType = form.template_type === "email";
  const [tab, setTab] = useState<"editor" | "preview">("editor");
  const [previewHtml, setPreviewHtml] = useState<{ subject: string; body: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);

  // Track caret positions so the variable picker can insert at the
  // right spot. For Quill we use its API; for subject (plain input)
  // and the plain-text body textarea we keep the selection on the
  // wrapped element.
  const quillRef = useRef<ReactQuill | null>(null);
  const subjectInputRef = useRef<HTMLInputElement | null>(null);
  const bodyTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [subjectCaret, setSubjectCaret] = useState<number>(0);
  const [bodyCaret, setBodyCaret]       = useState<number>(0);

  // ── Auto-fill slug from name on initial typing only ────────────
  // Once the user has manually edited the slug we leave it alone.
  const [slugTouched, setSlugTouched] = useState<boolean>(isEdit);
  useEffect(() => {
    if (slugTouched) return;
    const auto = form.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);
    setForm(f => ({ ...f, slug: auto }));
  }, [form.name, slugTouched]);

  // Category dropdown re-options whenever the type changes so call_log
  // categories don't appear under email and vice versa. Falls back to
  // the flat list when meta hasn't loaded the per-type map yet.
  const categoryOptions = useMemo(() => {
    if (meta?.categories_by_type?.[form.template_type]) {
      return meta.categories_by_type[form.template_type];
    }
    return meta?.categories ?? [];
  }, [meta, form.template_type]);

  const typeOptions       = meta?.types        ?? [];
  const visibilityOptions = meta?.visibilities ?? [];
  const variables = meta?.variables ?? [];

  // When the rep flips the type, the previously-selected category may
  // not exist on the new type's list. Snap to the first valid one + apply
  // the type's recommended visibility default. We don't touch visibility
  // when the rep has already overridden it for this row in the session.
  const handleTypeChange = (next: CommunicationTemplateType) => {
    setForm(f => {
      const allowed = meta?.categories_by_type?.[next] ?? [];
      const validCategory = allowed.find(c => c.value === f.category)
        ? f.category
        : (allowed[0]?.value ?? "general");
      const recommendedVisibility = meta?.default_visibility_by_type?.[next] ?? f.visibility;
      return {
        ...f,
        template_type: next,
        category: validCategory,
        visibility: recommendedVisibility,
        // Email types ship a subject; other types don't. Clear stale
        // subject text so the row doesn't carry an orphaned subject.
        subject: next === "email" ? f.subject : "",
      };
    });
  };

  const variableGroups = useMemo(() => {
    const groups: Record<string, typeof variables> = {};
    for (const v of variables) {
      if (!groups[v.group]) groups[v.group] = [];
      groups[v.group].push(v);
    }
    return groups;
  }, [variables]);

  /* ── Insert helpers ─────────────────────────────────────────── */

  /** Insert `{{key}}` at the current caret position in whichever
   *  field had focus last: subject input, plain-text body textarea
   *  (activity types), or Quill body (email type). Defaulting to the
   *  body keeps the most-common case fast. */
  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;

    const subjEl = subjectInputRef.current;
    if (subjEl && document.activeElement === subjEl) {
      const start = subjEl.selectionStart ?? subjectCaret ?? form.subject.length;
      const end   = subjEl.selectionEnd   ?? start;
      const next  = form.subject.slice(0, start) + token + form.subject.slice(end);
      setForm(f => ({ ...f, subject: next }));
      // Restore caret after React's re-render.
      requestAnimationFrame(() => {
        const el = subjectInputRef.current;
        if (el) {
          const pos = start + token.length;
          el.focus(); el.setSelectionRange(pos, pos);
          setSubjectCaret(pos);
        }
      });
      return;
    }

    // Plain-text body — applies when template_type !== 'email'.
    if (!isEmailType) {
      const el = bodyTextAreaRef.current;
      const start = el?.selectionStart ?? bodyCaret ?? form.body.length;
      const end   = el?.selectionEnd   ?? start;
      const next  = form.body.slice(0, start) + token + form.body.slice(end);
      setForm(f => ({ ...f, body: next }));
      requestAnimationFrame(() => {
        const target = bodyTextAreaRef.current;
        if (target) {
          const pos = start + token.length;
          target.focus(); target.setSelectionRange(pos, pos);
          setBodyCaret(pos);
        }
      });
      return;
    }

    // HTML body — Quill (email type only).
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const sel = editor.getSelection(true);
      const at  = sel?.index ?? editor.getLength();
      editor.insertText(at, token, "user");
      editor.setSelection(at + token.length, 0);
      setForm(f => ({ ...f, body: editor.root.innerHTML }));
    }
  };

  /* ── Preview ────────────────────────────────────────────────── */

  const loadPreview = async () => {
    if (!isEdit || !template) {
      // Local merge for unsaved templates — paint sample values from
      // the variable schema so the preview shows something useful.
      const ctx: Record<string, string> = {};
      for (const v of variables) ctx[v.key] = v.example;
      setPreviewHtml({
        subject: mergeLocal(form.subject, ctx),
        body:    mergeLocal(form.body, ctx),
      });
      return;
    }
    try {
      const p = await previewEmailTemplate(template.id, null);
      setPreviewHtml({ subject: p.subject, body: p.body });
    } catch (e: any) {
      setError(e?.message || "Preview failed.");
    }
  };

  useEffect(() => {
    if (tab === "preview") loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /* ── Save ───────────────────────────────────────────────────── */

  const handleSave = async () => {
    setError(null);
    if (!form.name.trim())    { setError("Template name is required."); return; }
    if (isEmailType && !form.subject.trim()) {
      setError("Subject is required for email templates."); return;
    }
    // Body emptiness check has to handle both shapes: Quill's idle
    // placeholder "<p><br></p>" for HTML bodies, and just whitespace
    // for plain-text activity templates.
    const bodyStripped = form.body.replace(/<[^>]+>/g, "").trim();
    if (!bodyStripped) { setError("Body is required."); return; }

    const payload: IEmailTemplateInput = {
      name: form.name.trim(),
      slug: form.slug.trim() || undefined,
      // Only ship subject for email — sending an empty string on
      // activity types would fail the server validation.
      subject: isEmailType ? form.subject.trim() : undefined,
      description: form.description.trim() || undefined,
      body: form.body,
      category: form.category || undefined,
      template_type: form.template_type,
      visibility: form.visibility,
      is_active: form.is_active,
      is_default: form.is_default,
    };

    setSaving(true);
    try {
      const tpl = isEdit && template
        ? await updateEmailTemplate(template.id, payload)
        : await createEmailTemplate(payload);
      onSaved(tpl, isEdit ? "Template updated." : "Template created.");
    } catch (e: any) {
      setError(e?.message || "Save failed.");
    }
    setSaving(false);
  };

  /* ── Render ─────────────────────────────────────────────────── */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: NAVY }}>
              {isEdit ? "Edit Template" : "New Communication Template"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              Variables resolve at send time from the lead, customer, quote, and company context.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "1fr 280px" }, minHeight: 540 }}>
          {/* ── LEFT pane: editor / preview ──────────────────── */}
          <Box sx={{ borderRight: { md: `1px solid ${HAIRLINE}` } }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ px: 2, borderBottom: `1px solid ${HAIRLINE}`, minHeight: 38 }}
              TabIndicatorProps={{ style: { backgroundColor: NAVY } }}
            >
              <Tab value="editor"  label="Editor"  sx={{ textTransform: "none", fontWeight: 700, minHeight: 38 }} />
              <Tab value="preview" label="Preview" sx={{ textTransform: "none", fontWeight: 700, minHeight: 38 }} />
            </Tabs>

            {tab === "editor" ? (
              <Stack gap={1.4} sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.2}>
                  <TextField
                    label="Template Name *"
                    size="small" fullWidth
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  />
                  <TextField
                    label="Slug / Key"
                    size="small" fullWidth
                    value={form.slug}
                    onChange={(e) => { setSlugTouched(true); setForm(f => ({ ...f, slug: e.target.value })); }}
                    InputProps={{
                      endAdornment: !slugTouched && form.slug ? null : (
                        <InputAdornment position="end">
                          <Tooltip title="Re-generate from name" arrow>
                            <IconButton
                              size="small"
                              onClick={() => { setSlugTouched(false); }}
                            >
                              <RestartAltIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    helperText={slugTouched ? " " : "Auto-generated from name; click ↻ to reset"}
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} gap={1.2}>
                  <TextField
                    label="Type"
                    select size="small" fullWidth
                    value={form.template_type}
                    onChange={(e) => handleTypeChange(e.target.value as CommunicationTemplateType)}
                    helperText="Drives where this template appears: email composer, call log, notes…"
                  >
                    {typeOptions.map(t => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Visibility"
                    select size="small" fullWidth
                    value={form.visibility}
                    onChange={(e) => setForm(f => ({ ...f, visibility: e.target.value as CommunicationTemplateVisibility }))}
                    helperText="Customer-facing = the customer sees this. Internal/private = staff-only."
                  >
                    {visibilityOptions.map(v => (
                      <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} gap={1.2}>
                  <TextField
                    label="Category"
                    select size="small" fullWidth
                    value={form.category}
                    onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  >
                    {categoryOptions.map(c => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Short Description"
                    size="small" fullWidth
                    value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Shown in the picker list — keep it short"
                  />
                </Stack>

                {isEmailType && (
                  <TextField
                    label="Subject *"
                    size="small" fullWidth
                    value={form.subject}
                    onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
                    onFocus={(e) => setSubjectCaret(e.target.selectionStart ?? 0)}
                    onClick={(e) => setSubjectCaret((e.target as HTMLInputElement).selectionStart ?? 0)}
                    onKeyUp={(e) => setSubjectCaret((e.target as HTMLInputElement).selectionStart ?? 0)}
                    inputRef={(el) => (subjectInputRef.current = el)}
                    helperText="Use {{variables}} from the picker on the right."
                  />
                )}

                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY }}>
                      Body *
                    </Typography>
                    {/* Generate-with-AI launcher — sits inline with the
                        Body label so it reads as part of the editor
                        affordance, not the dialog actions.
                        Email-only for now; the activity AI prompts
                        ship in Phase 3 with their own dialog. */}
                    {isEmailType && (
                      <Button
                        size="small"
                        onClick={() => setAiOpen(true)}
                        startIcon={<AutoAwesomeIcon sx={{ fontSize: 16, color: "#C9A961" }} />}
                        sx={{
                          textTransform: "none", fontWeight: 700, fontSize: 11.5,
                          color: NAVY,
                          border: `1px solid rgba(201,169,97,0.45)`,
                          bgcolor: "#FBF7EE",
                          px: 1.2, py: 0.3, borderRadius: "8px",
                          "&:hover": { bgcolor: "#FBF0D7", borderColor: "#C9A961" },
                        }}
                      >
                        Generate with AI
                      </Button>
                    )}
                  </Stack>
                  {isEmailType ? (
                    <Box sx={{
                      "& .ql-editor": { minHeight: 240, fontSize: 13.5 },
                      "& .ql-toolbar": { borderTopLeftRadius: 6, borderTopRightRadius: 6 },
                      "& .ql-container": { borderBottomLeftRadius: 6, borderBottomRightRadius: 6 },
                    }}>
                      <ReactQuill
                        ref={quillRef}
                        modules={QUILL_MODULES}
                        value={form.body}
                        onChange={(v) => setForm(f => ({ ...f, body: v }))}
                      />
                    </Box>
                  ) : (
                    // Activity-type bodies are plain text — call/note/
                    // meeting notes never need HTML and a heavy editor
                    // would slow Quick Actions down (decision #9).
                    // {{variables}} still work via the right-pane picker
                    // since the resolver is content-agnostic.
                    <TextField
                      multiline
                      minRows={10}
                      maxRows={20}
                      fullWidth
                      placeholder="Write the template body. Use {{variables}} from the picker on the right."
                      value={form.body}
                      onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
                      onFocus={(e) => setBodyCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                      onClick={(e) => setBodyCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                      onKeyUp={(e) => setBodyCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                      inputRef={(el) => (bodyTextAreaRef.current = el)}
                      sx={{ "& .MuiInputBase-root": { fontSize: 13.5, lineHeight: 1.6, fontFamily: "inherit" } }}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 0.5 }} />
                <Stack direction="row" gap={3} flexWrap="wrap" useFlexGap>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_active}
                        onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                      />
                    }
                    label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Active</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_default}
                        onChange={(e) => setForm(f => ({ ...f, is_default: e.target.checked }))}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        Default for this type + category
                      </Typography>
                    }
                  />
                </Stack>
              </Stack>
            ) : (
              <Box sx={{ p: 3 }}>
                {previewHtml ? (
                  <Stack gap={1.5}>
                    {isEmailType && (
                      <>
                        <Box>
                          <Typography sx={{ fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.08em", fontWeight: 700 }}>
                            SUBJECT
                          </Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                            {previewHtml.subject || "(empty subject)"}
                          </Typography>
                        </Box>
                        <Divider />
                      </>
                    )}
                    <Box>
                      <Typography sx={{ fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.08em", fontWeight: 700, mb: 0.5 }}>
                        BODY
                      </Typography>
                      {isEmailType ? (
                        // Email body — HTML, rendered as such.
                        <Box
                          className="email-template-preview"
                          sx={{
                            fontSize: 13.5, color: "#1e293b",
                            "& p": { mt: 0, mb: 1 },
                          }}
                          dangerouslySetInnerHTML={{ __html: previewHtml.body || "<p><em>(empty body)</em></p>" }}
                        />
                      ) : (
                        // Activity body — plain text. Preserve line
                        // breaks via white-space: pre-wrap so the
                        // preview matches what the rep typed and
                        // what eventually persists on the activity row.
                        <Box
                          sx={{
                            fontSize: 13.5, color: "#1e293b",
                            whiteSpace: "pre-wrap",
                            fontFamily: "inherit",
                          }}
                        >
                          {previewHtml.body || "(empty body)"}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 13, color: "#64748b", fontStyle: "italic" }}>
                    Loading preview…
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* ── RIGHT pane: variable picker ──────────────────── */}
          <Box sx={{ p: 2, bgcolor: "#fafbfc", maxHeight: "70vh", overflow: "auto" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY, mb: 0.4 }}>
              Variables
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#64748b", mb: 1.5 }}>
              Click to insert into the focused field.
            </Typography>
            <Stack gap={1.5}>
              {Object.entries(variableGroups).map(([group, items]) => (
                <Box key={group}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 700, color: "#94a3b8",
                    letterSpacing: "0.08em", mb: 0.5, textTransform: "uppercase",
                  }}>
                    {group}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                    {items.map(v => (
                      <Tooltip key={v.key} title={`e.g. "${v.example}"`} arrow>
                        <Chip
                          size="small"
                          label={v.label}
                          onClick={() => insertVariable(v.key)}
                          sx={{
                            fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                            bgcolor: "#fff", border: `1px solid ${HAIRLINE}`,
                            "&:hover": { bgcolor: "#fff7e8", borderColor: "#c9a961" },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      {aiOpen && (
        <AIGenerateDialog
          category={form.category}
          categoryLabel={categoryOptions.find(c => c.value === form.category)?.label ?? form.category}
          name={form.name}
          subject={form.subject}
          hasExistingBody={Boolean(form.body && form.body.replace(/<[^>]+>/g, "").trim().length > 0)}
          onClose={() => setAiOpen(false)}
          onApply={(result: IAIGenerateResult) => {
            // Replace body in form state. If we have a live Quill
            // editor, also push the HTML through its API so the
            // toolbar state stays in sync with the new content.
            setForm(f => ({ ...f, body: result.body }));
            const editor = quillRef.current?.getEditor?.();
            if (editor && typeof editor.clipboard?.dangerouslyPasteHTML === "function") {
              editor.setContents([] as any);
              editor.clipboard.dangerouslyPasteHTML(0, result.body, "user");
            }
            setAiOpen(false);
          }}
        />
      )}

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {error && (
          <Typography sx={{ flex: 1, color: "#dc2626", fontSize: 12, fontWeight: 700 }}>
            {error}
          </Typography>
        )}
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            textTransform: "none", fontWeight: 700,
            bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" },
          }}
        >
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditor;

/** Client-side {{var}} substitution used for the unsaved-template
 *  preview path. Mirrors the server resolver's "leave unknown tokens
 *  alone" behaviour so the rep can spot typos. */
function mergeLocal(text: string, ctx: Record<string, string>): string {
  if (!text) return "";
  return text.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (m, k) =>
    Object.prototype.hasOwnProperty.call(ctx, k) ? String(ctx[k] ?? "") : m
  );
}
